<script setup>

    import { ref, onMounted, onBeforeUnmount } from 'vue'
    import { RouterView } from 'vue-router'
    import { storeToRefs } from 'pinia'

    import { useAuthStore, useConfigStore, useViewStore, useSmsStore } from '@/Stores'

    import Notification from '@/Components/App/Notification.vue'
    import LattePageNav from '@/Components/App/LattePageNav.vue'
    import LatteActionModal from '@/Components/App/LatteActionModal.vue'
    import LatteOnboardingTour from '@/Components/App/LatteOnboardingTour.vue'

    import { util } from '@/Helpers'

    import AuthenticatedLayout from '@/Layouts/AuthenticatedLayout.vue'
    import GuestLayout from '@/Layouts/GuestLayout.vue'

    import Loader from '@/Components/App/Loader.vue'
    import { ArrowUturnUpIcon } from '@heroicons/vue/20/solid'
    import DraggableDialog from '@/Components/App/DraggableDialog.vue'

    const configStore = useConfigStore()
    const authStore = useAuthStore()
    const viewStore = useViewStore()
    const smsStore = useSmsStore()

    const { config } = storeToRefs(configStore)
    const { auth, user } = storeToRefs(authStore)
    const { scrollDist, isEmbedded } = storeToRefs(viewStore)
    const { showSmsModal } = storeToRefs(smsStore)
    const data = ref({})

    const mountEl = document.querySelector("#app")

    config.value = { ...config.value, ...JSON.parse(mountEl.dataset.config) }
    data.value = JSON.parse(mountEl.dataset.data)

    authStore.user = mountEl.dataset.user ? JSON.parse(mountEl.dataset.user) : null
    authStore.auth.authenticated = authStore.user && authStore.user.user_id ? true : false
    authStore.auth.loading = false

    const handleScroll = (e) => {
        scrollDist.value = window.scrollY
    }

    onMounted(() => {
        isEmbedded.value = (window.location.href.includes('/embedded/') || data.value?.redirect?.includes('/embedded/'))
        document.addEventListener('scroll', handleScroll)

        if (isEmbedded.value) {
            setUpEmbeddedParentHandleDocumentEvents()
        }

        setUpParentHandleEvent()
    })

    onBeforeUnmount(() => {
        document.removeEventListener('scroll', handleScroll)
    })

    function setUpEmbeddedParentHandleDocumentEvents() {
        window.addEventListener('message', receiveMessage, false);

        function closeDateTimePickerMenu() {
            const escEvent = new KeyboardEvent('keydown', {
                key: 'Escape',
                code: 'Escape',
                keyCode: 27, // Deprecated, but some older browsers may use it
                which: 27,   // Deprecated, but included for compatibility
                bubbles: true,
            })
            const element = document.querySelector('.dp__menu')
            if (element) {
                element.dispatchEvent(escEvent)
            }
        }

        function receiveMessage(event) {
            var message = event.data;

            if (message.type == 'parent-document-click') {
                closeDateTimePickerMenu()
            } else if (message.type == 'parent-document-scroll') {
                viewStore.handleParentScroll(message)
            }
        }
    }

    function setUpParentHandleEvent() {
        window.addEventListener('message', (event) => {
            if (event.data.action == 'smsIframeLoaded') {
                smsStore.registerSmsIframeLoaded()
            }
        }, false);
    }

    const coffeeSmsIframeUrl = import.meta.env.VITE_COFFEE_BASEURL + '/staff/sms/iframe'

</script>

<style>

    .dp__theme_light {
        --dp-primary-color: #008AAB;
        --dp-primary-disabled-color: #005D73;
        --dp-success-color: #3F911A;
        --dp-success-color-disabled: #347715;
        --dp-danger-color: #AB2100;
        --dp-marker-color: #AB2100;
        --dp-input-padding: 8px 12px;
        --dp-font-size: 0.8rem;
        --dp-time-font-size: 1.25rem;
        --dp-action-buttons-padding: 4px 35px;
    }

    .theme-alabaster .dp__theme_light {
        --dp-background-color: rgb(227 214 197 / 0.5);
        --dp-hover-color: #e3d6c5;
        --dp-border-color: #e3d6c5;
        --dp-border-color-hover: #a8a29e
    }

    .theme-alabaster .dp__theme_light .dp__input:hover {
        --dp-background-color: rgb(227 214 197 / 1);
        transition: none;
    }

    .dp__theme_dark {
        --dp-background-color: #e3d6c5;
        --dp-border-color: #a8a29e;
        --dp-primary-color: #008AAB;
        --dp-primary-disabled-color: #005D73;
        --dp-success-color: #3F911A;
        --dp-success-color-disabled: #347715;
        --dp-danger-color: #AB2100;
        --dp-marker-color: #AB2100;
        --dp-input-padding: 8px 12px;
        --dp-font-size: 0.8rem;
        --dp-time-font-size: 1.25rem;
        --dp-action-buttons-padding: 4px 35px;
    }

    .dp__time_col_block {
        transform: rotate(180deg);
    }
    .dp__time_display_block {
        transform: rotate(180deg);
    }

    .dp--menu-wrapper {
        z-index: 8000;
    }

    /* Tour styling */
    :root {
        --v-onboarding-overlay-z: 19000;
        --v-onboarding-step-arrow-background: white;
        --v-onboarding-step-arrow-size: 10px;
        --v-onboarding-step-z: 20000;
    }

</style>

<template>

    <div>

        <AuthenticatedLayout
            v-if="auth.authenticated && user"
            :config="config"
            :user="user"
            :data="data"
        >

            <Loader
                v-if="auth.loading"
                class="my-36"
            />

            <RouterView
                v-else
                :data="data"
            />

        </AuthenticatedLayout>

        <GuestLayout
            v-else
            :config="config"
            :data="data"
        >

            <Loader
                v-if="auth.loading"
                class="my-36"
            />

            <RouterView
                v-else
                :data="data"
            />

        </GuestLayout>

        <Notification
            class="z-notifications"
        />

        <LatteActionModal />

        <DraggableDialog title="Messages" :show="showSmsModal" @close="smsStore.handleSmsClose">
            <iframe id="coffee-sms" :src="coffeeSmsIframeUrl" class="w-[600px] h-[400px]"></iframe>
        </DraggableDialog>

        <button
            @click="util.scrollToElement('app')"
            class="fixed z-floating-nav right-5 p-4 border-0 w-14 h-14 rounded-full shadow-md bg-lifeworx-blue-600 hover:bg-lifeworx-blue-700 text-white text-lg font-semibold transition-all duration-300"
            :class="{
                'bottom-5': scrollDist > 500,
                '-bottom-28 ': scrollDist <= 500,
                }"
        >
            <ArrowUturnUpIcon class="w-6 h-6" />
            <span class="sr-only">Back to top</span>
        </button>

        <LattePageNav />

        <LatteOnboardingTour />

    </div>

</template>
